"use client"
import { font_title_secularOne } from "@/app/ui/fonts";
import { cn } from "@/lib/utils";
import { MouseEventHandler, ReactNode, useId } from "react";
import { useFormStatus } from "react-dom";
import Spinner from "../utils/Spinner";

interface ButtonProps {
    children: ReactNode,
    type?: "button" | "submit" | "reset" | undefined,
    color?: "nfPurple" | "nfBlue" | "red" | "gold" | "gray" | "green",
    className?: string,
    disabled?: boolean,
    onClick?: MouseEventHandler<HTMLButtonElement>,
    isLoading?: boolean
}


export default function ButtonNF({ isLoading = false, children, type, color = "nfPurple", className, disabled = false, onClick }: ButtonProps) {
    const { pending } = useFormStatus();
    const id = useId()


    let colors = {
        light: "",
        color: "",
        base: "",
        outline: "",
        addedClasses: ""
    }

    switch (color) {
        case "nfPurple":
            colors.light = "bg-nfBlue-300"
            colors.color = "bg-nfBlue-500"
            colors.base = "bg-nfPurple-300"
            colors.outline = "outline-nfBlue-500/70"
            break;
        case "nfBlue":
            colors.light = "bg-nfPurple-300"
            colors.color = "bg-nfPurple-300"
            colors.base = "bg-nfBlue-500"
            colors.outline = "outline-nfPurple-400/70"
            break;
        case "red":
            colors.light = "bg-red-400"
            colors.color = "bg-red-600"
            colors.base = "bg-red-700"
            colors.outline = "outline-red-600/70"
            colors.addedClasses = "py-0.5 px-1"
            break;
        case "gold":
            colors.light = "bg-amber-200"
            colors.color = "bg-yellow-300"
            colors.base = "bg-amber-400"
            colors.outline = "outline-yellow-500/70"
            break;
        case "gray":
            colors.light = "bg-gray-300"
            colors.color = "bg-gray-500"
            colors.base = "bg-gray-400/60"
            colors.outline = "outline-gray-600/70"
            break;
        case "green":
            colors.light = "bg-lime-200"
            colors.color = "bg-lime-400"
            colors.base = "bg-lime-500"
            colors.outline = "outline-lime-500/70"
            break;
    }

    return <button disabled={ disabled || isLoading || pending } type={ type } onClick={ onClick }
        aria-describedby={ id }
        className={
            cn([`focus-visible:outline-none focus-visible:ring-2 focus:ring-nfBlue-200 focus-visible:ring-offset-2`,
                `overflow-hidden py-2 px-3 text-white rounded-md cursor-pointer relative transition-all outline outline-0 hover:outline-4 active:outline-2 active:brightness-125 group`,
                colors.base, colors.outline, colors.addedClasses, className, disabled && "opacity-60",
            ])
        }
        style={ {
            transitionDuration: "1s",
            transitionTimingFunction: "cubic-bezier(0, 0.32, 0, 1.7)"
        } }>
        <span className="z-10 absolute w-[150%] sm:w-[120%] h-[-webkit-fill-available] -top-[8em] -left-0 bg-white rotate-12 transform scale-x-0 group-hover:scale-x-100 transition-transform group-hover:duration-500 duration-1000 origin-left"></span>
        <span className={ `z-10 absolute w-[150%] sm:w-[120%] h-[-webkit-fill-available] -top-[8em] -left-0 ${colors.light} rotate-12 transform scale-x-0 group-hover:scale-x-100 transition-transform group-hover:duration-700 duration-700 origin-left` }></span>
        <span className={ `z-10 absolute w-[150%] sm:w-[120%] h-[-webkit-fill-available] -top-[8em] -left-0  ${colors.color} rotate-12 transform scale-x-0 group-hover:scale-x-100 transition-transform group-hover:duration-1000 duration-500 origin-left` }></span>
        <div id={ id } className={ cn(font_title_secularOne.className, "z-20 flex gap-1 relative h-[inherit] items-center justify-center w-[100%] pointer-events-none leading-[1em]") }
            style={ { filter: "drop-shadow(0.05em 0.14em 0.15em rgb(0 0 0 / 0.7)) drop-shadow(0.08em 0.13em 0em rgb(0 0 0 / 0.55))" } }>
            { (isLoading || pending) && <Spinner className={ colors.light.replace("bg", "text") } /> || children }
        </div>
    </button>
}
